import request from '@/utils/request';

/**
 * 获取指定桥梁下的所有告警记录
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     bridgeId: number,
 *     areaId: number,
 *     alertTime: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     sourceType: string,
 *     sourceId: string,
 *     sourceName: string,
 *     ruleId: number,
 *     ruleName: string,
 *     ruleType: string,
 *     longitude: number,
 *     latitude: number,
 *     status: number,
 *     handleUser: number,
 *     processor: string,
 *     handleTime: string,
 *     handleResult: string,
 *     sourceData: string,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }>
 * }>} 告警记录列表
 */
export function getAllAlertRecords(bridgeId) {
  return request({
    url: '/alert-record',
    method: 'get',
    params: { bridgeId }
  });
}

/**
 * 获取告警记录列表（分页）
 * @param {Object} params 查询参数
 * @param {number} [params.pageNum] 页码
 * @param {number} [params.pageSize] 每页数量
 * @param {string} [params.sourceId] 来源ID
 * @param {string} [params.ruleType] 规则类型
 * @param {string} [params.alertLevel] 告警级别
 * @param {number} [params.status] 处理状态(0-未处理,1-已处理,2-已忽略)
 * @param {number} [params.areaId] 区域ID
 * @param {number} [params.bridgeId] 桥梁ID
 * @param {string} [params.startTimeStr] 开始时间(yyyy-MM-dd HH:mm:ss)
 * @param {string} [params.endTimeStr] 结束时间(yyyy-MM-dd HH:mm:ss)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeId: number,
 *       areaId: number,
 *       alertTime: string,
 *       alertLevel: string,
 *       alertMessage: string,
 *       sourceType: string,
 *       sourceId: string,
 *       sourceName: string,
 *       ruleId: number,
 *       ruleName: string,
 *       ruleType: string,
 *       longitude: number,
 *       latitude: number,
 *       status: number,
 *       handleUser: number,
 *       processor: string,
 *       handleTime: string,
 *       handleResult: string,
 *       sourceData: string,
 *       createTime: string,
 *       updateTime: string,
 *       isDeleted: boolean
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的告警记录列表
 */
export function getAlertRecordList(params) {
  return request({
    url: '/alert-record/list',
    method: 'get',
    params
  });
}

/**
 * 获取人工处理记录列表（已处理/已忽略，后端已排除 SYSTEM_AUTO 系统自动处理）
 * @param {Object} params - { pageNum, pageSize, bridgeId, mmsiOrName, ruleType, status, startTimeStr, endTimeStr }
 */
export function getHandledAlertRecordList(params) {
  return request({
    url: '/alert-record/handled-list',
    method: 'get',
    params
  });
}

/**
 * 获取指定桥梁下最近的告警记录
 * @param {number} bridgeId 桥梁ID
 * @param {number} [limit] 限制数量
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     bridgeId: number,
 *     areaId: number,
 *     alertTime: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     sourceType: string,
 *     sourceId: string,
 *     sourceName: string,
 *     ruleId: number,
 *     ruleName: string,
 *     ruleType: string,
 *     longitude: number,
 *     latitude: number,
 *     status: number,
 *     handleUser: number,
 *     processor: string,
 *     handleTime: string,
 *     handleResult: string,
 *     sourceData: string,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }>
 * }>} 最近的告警记录列表
 */
export function getRecentAlertRecords(bridgeId, limit) {
  return request({
    url: '/alert-record/recent',
    method: 'get',
    params: { bridgeId, limit }
  });
}

/**
 * 获取指定桥梁下未处理的告警记录列表
 * @param {number} bridgeId 桥梁ID
 * @param {string} [alertTypes] 告警类型列表，用逗号分隔，如YAW,SPEED,HIGH,BLACKLIST
 * @param {number} [limitCount] 限制返回记录数量
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     bridgeId: number,
 *     areaId: number,
 *     alertTime: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     sourceType: string,
 *     sourceId: string,
 *     sourceName: string,
 *     ruleId: number,
 *     ruleName: string,
 *     ruleType: string,
 *     longitude: number,
 *     latitude: number,
 *     status: number,
 *     handleUser: number,
 *     processor: string,
 *     handleTime: string,
 *     handleResult: string,
 *     sourceData: string,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }>
 * }>} 未处理的告警记录列表
 */
export function getUnhandledAlertRecords(bridgeId, alertTypes, limitCount) {
  const params = { bridgeId };
  if (alertTypes) {
    params.alertTypes = alertTypes;
  }
  if (limitCount) {
    params.limitCount = limitCount;
  }
  
  return request({
    url: '/alert-record/unhandled',
    method: 'get',
    params
  });
}

/**
 * 根据ID获取告警记录信息
 * @param {number} id 告警记录ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeId: number,
 *     areaId: number,
 *     alertTime: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     sourceType: string,
 *     sourceId: string,
 *     sourceName: string,
 *     ruleId: number,
 *     ruleName: string,
 *     ruleType: string,
 *     longitude: number,
 *     latitude: number,
 *     status: number,
 *     handleUser: number,
 *     processor: string,
 *     handleTime: string,
 *     handleResult: string,
 *     sourceData: string,
 *     createTime: string,
 *     updateTime: string,
 *     isDeleted: boolean
 *   }
 * }>} 告警记录信息
 */
export function getAlertRecordById(id) {
  return request({
    url: `/alert-record/${id}`,
    method: 'get'
  });
}

/**
 * 处理告警记录
 * @param {number} id 告警记录ID
 * @param {number} status 处理状态(1-已处理,2-已忽略)
 * @param {string} [handleResult] 处理结果
 * @param {Array<File>} [images] 附件图片文件列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Object
 * }>} 处理结果
 */
export function handleAlertRecord(id, status, handleResult, images = []) {
  console.log('=== 告警处理API调用 ===');
  console.log('参数:', { id, status, handleResult, imageCount: images?.length || 0 });
  
  // 构建FormData，包含所有参数和文件
  const formData = new FormData();
  
  // 添加必需参数 - 确保转换为字符串
  formData.append('status', String(status));
  console.log('已添加status:', status);
  
  // 添加可选参数
  if (handleResult) {
    formData.append('handleResult', handleResult);
    console.log('已添加handleResult:', handleResult);
  }
  
  // 添加图片文件（如果有的话）
  if (images && images.length > 0) {
    images.forEach((file, index) => {
      formData.append('images', file);
      console.log(`已添加图片 ${index + 1}:`, file.name, file.size, file.type);
    });
    console.log(`总共添加了 ${images.length} 张图片`);
  }
  
  // 调试：打印FormData内容
  console.log('FormData 内容:');
  for (let pair of formData.entries()) {
    if (pair[1] instanceof File) {
      console.log(`${pair[0]}: File(${pair[1].name}, ${pair[1].size}字节)`);
    } else {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
  }
  
  return request({
    url: `/alert-record/${id}/handle`,
    method: 'post',
    data: formData
    // 重要：不设置任何headers，让浏览器自动处理multipart/form-data
  });
}

/**
 * 批量处理告警记录
 * @param {Array<number>} ids 告警记录ID数组
 * @param {number} status 处理状态(1-已处理,2-已忽略)
 * @param {string} [handleResult] 处理结果
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Object
 * }>} 处理结果
 */
export function batchHandleAlertRecords(ids, status, handleResult) {
  return request({
    url: '/alert-record/batch-handle',
    method: 'put',
    params: { status, handleResult },
    data: ids
  });
}

/**
 * 删除告警记录
 * @param {number} id 告警记录ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Object
 * }>} 删除结果
 */
export function deleteAlertRecord(id) {
  return request({
    url: `/alert-record/${id}`,
    method: 'delete'
  });
}

/**
 * 批量删除告警记录
 * @param {Array<number>} ids 告警记录ID数组
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Object
 * }>} 删除结果
 */
export function batchDeleteAlertRecords(ids) {
  return request({
    url: '/alert-record/batch',
    method: 'delete',
    data: ids
  });
}

/**
 * 按级别统计指定桥梁下的告警记录
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {[key: string]: number}
 * }>} 统计结果
 */
export function getAlertStatisticsByLevel(bridgeId) {
  return request({
    url: '/alert-record/statistics/by-level',
    method: 'get',
    params: { bridgeId }
  });
}

/**
 * 按状态统计指定桥梁下的告警记录
 * @param {number} bridgeId 桥梁ID
 * @param {string} [startTime] 开始时间（yyyy-MM-dd HH:mm:ss），不传则查全部
 * @param {string} [endTime] 结束时间（yyyy-MM-dd HH:mm:ss），不传则查全部
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {[key: string]: number}
 * }>} 统计结果
 */
export function getAlertStatisticsByStatus(bridgeId, startTime, endTime) {
  const params = { bridgeId }
  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime
  return request({
    url: '/alert-record/statistics/by-status',
    method: 'get',
    params
  });
}

/**
 * 按类型统计指定桥梁下的告警记录
 * @param {number} bridgeId 桥梁ID
 * @param {string} [startTime] 开始时间（yyyy-MM-dd HH:mm:ss），不传则查全部
 * @param {string} [endTime] 结束时间（yyyy-MM-dd HH:mm:ss），不传则查全部
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {[key: string]: number}
 * }>} 统计结果
 */
export function getAlertStatisticsByType(bridgeId, startTime, endTime) {
  const params = { bridgeId }
  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime
  return request({
    url: '/alert-record/statistics/by-type',
    method: 'get',
    params
  });
}

/**
 * 按时间段统计指定桥梁下的告警记录
 * @param {Object} params 查询参数
 * @param {number} params.bridgeId 桥梁ID
 * @param {number} params.startTime 开始时间戳
 * @param {number} params.endTime 结束时间戳
 * @param {string} [params.interval] 时间间隔 (hour/day/week/month)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     time: string,
 *     timeLabel: string,
 *     count: number,
 *     details: Object
 *   }>
 * }>} 时间统计结果
 */
export function getAlertStatisticsByTime(params) {
  return request({
    url: '/alert-record/statistics/by-time',
    method: 'get',
    params
  });
} 